import {BookedStatus} from '@shared/core';
import {endOfDay, parse} from 'date-fns';

export interface BookedRange {
  employeeId: string;
  range: {
    start: Date;
    end: Date;
  };
}

export type BookingFilterRange = {
  start: Date;
  end: Date;
};

const rangesOverlap = (aStart: Date, aEnd: Date, bStart: Date, bEnd: Date): boolean => {
  return aStart <= bEnd && aEnd >= bStart;
};

export const getRelevantBookedRange = (
  bookedStatus: BookedRange[],
  filterRange?: BookingFilterRange | null,
): BookedRange | null => {
  if (!bookedStatus?.length) {
    return null;
  }

  if (filterRange?.start && filterRange?.end) {
    return (
      bookedStatus.find((status) =>
        rangesOverlap(status.range.start, status.range.end, filterRange.start, filterRange.end),
      ) || null
    );
  }

  const currentDate = new Date();

  const overlappingBooking = bookedStatus.find((status) => {
    return currentDate >= status.range.start && currentDate <= status.range.end;
  });
  if (overlappingBooking) {
    return overlappingBooking;
  }

  const futureBookings = bookedStatus
    .filter((status) => status.range.start > currentDate)
    .sort((a, b) => new Date(a.range.start).getTime() - new Date(b.range.start).getTime());

  return futureBookings[0] || null;
};

export const getProcessedBookedRange = (bookedRangeArr: BookedStatus[]): BookedRange[] => {
  if (!bookedRangeArr?.length) {
    return [];
  }

  return bookedRangeArr?.map((status) => {
    return {
      employeeId: status.employee.id,
      range: {
        start: parse(status.range.startDay, 'dd.MM.yyyy', new Date()),
        end: endOfDay(parse(status.range.endDay, 'dd.MM.yyyy', new Date())),
      },
    };
  });
};

/** Name shown on the desk tile: booking overlapping filter (or today/next), not always [0]. */
export const getBookedStatusEmployeeName = (
  bookedStatus: BookedStatus[],
  filterRange?: BookingFilterRange | null,
): string | undefined => {
  if (!bookedStatus?.length) {
    return undefined;
  }

  const relevant = getRelevantBookedRange(getProcessedBookedRange(bookedStatus), filterRange);
  const holder = relevant && bookedStatus.find((status) => status.employee?.id === relevant.employeeId);

  return (holder || bookedStatus[0])?.employee?.fullName;
};

/** Bookings listed in the desk tooltip; filtered by date range when the filter is active. */
export const getBookedStatusesForTooltip = (
  bookedStatus: BookedStatus[],
  filterRange?: BookingFilterRange | null,
): BookedStatus[] => filterStatusesByDateRange(bookedStatus, filterRange);

const filterStatusesByDateRange = (
  statuses: BookedStatus[],
  filterRange?: BookingFilterRange | null,
): BookedStatus[] => {
  if (!statuses?.length) {
    return [];
  }

  if (!filterRange?.start || !filterRange?.end) {
    return statuses;
  }

  return statuses.filter((status) => {
    const [processed] = getProcessedBookedRange([status]);
    if (!processed) {
      return false;
    }

    return rangesOverlap(processed.range.start, processed.range.end, filterRange.start, filterRange.end);
  });
};

/** Pending requests listed in the desk tooltip; filtered by date range when the filter is active. */
export const getActiveRequestsForTooltip = (
  activeRequests: BookedStatus[],
  filterRange?: BookingFilterRange | null,
): BookedStatus[] => filterStatusesByDateRange(activeRequests, filterRange);
